import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, ShieldCheck, GraduationCap, School, Building2, Users, Sun, PawPrint, Heart } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Motion";

const INTERVENTIONS = [
  {
    icon: HeartHandshake,
    title: "Médiation animale",
    text: "Des séances avec le chien médiateur pour apaiser un groupe, stimuler la communication et recréer du lien — auprès d'enfants, d'adolescents ou de publics fragiles.",
  },
  {
    icon: ShieldCheck,
    title: "Prévention morsure",
    text: "Des ateliers concrets pour apprendre aux enfants à décoder le langage du chien, respecter ses limites et adopter les bons gestes — le meilleur moyen de prévenir les accidents.",
  },
  {
    icon: GraduationCap,
    title: "Éducation autour de l'animal",
    text: "Des programmes pédagogiques pour sensibiliser les enfants au respect de l'animal, à ses besoins et à une relation responsable et bienveillante.",
  },
  {
    icon: Heart,
    title: "Harcèlement scolaire",
    text: "Des séances de médiation animale pour redonner confiance aux enfants touchés par le harcèlement et recréer un climat apaisé, dans la classe comme en dehors.",
  },
];

const STRUCTURES = [
  { icon: School, label: "Écoles maternelles et élémentaires" },
  { icon: Users, label: "Associations et accueils de loisirs" },
  { icon: Building2, label: "Centres médico-sociaux" },
  { icon: Sun, label: "Structures accueillant enfants et adolescents" },
];

const STEPS = [
  { num: "01", title: "Premier contact", text: "Vous me présentez votre structure, votre public et vos objectifs. L'échange est gratuit et sans engagement." },
  { num: "02", title: "Programme sur mesure", text: "Je construis une proposition adaptée — intervention ponctuelle ou cycle de séances — avec un devis clair." },
  { num: "03", title: "Intervention sur site", text: "J'interviens dans vos locaux, accompagnée de Bueno, mon chien médiateur en formation, déjà formidable auprès des enfants." },
];

export default function Structures() {
  return (
    <div data-testid="structures-page">
      <PageHero
        testId="structures-hero"
        overline="Structures & professionnels"
        lines={[
          { text: "La médiation animale" },
          { text: "en école, association" },
          { text: "et centre médico-social.", accent: true },
        ]}
        intro="J'interviens aussi auprès des structures : médiation animale, prévention des morsures et éducation des enfants autour de l'animal. Interventions ponctuelles ou en cycle, sur devis."
      />

      {/* INTERVENTIONS */}
      <section className="px-6 pb-24 sm:px-10 md:pb-32 lg:px-16" data-testid="structures-interventions">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="overline-label">Mes interventions</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
              Quatre formats, <span className="italic text-terracotta">un même fil : le vivant.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {INTERVENTIONS.map((item, i) => (
              <Reveal key={item.title} delay={0.1 * i} y={40}>
                <div
                  className="flex h-full flex-col rounded-[2rem] border border-stone-border bg-white/70 p-10 transition-transform duration-300 hover:-translate-y-1.5"
                  data-testid={`intervention-card-${i + 1}`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                    <item.icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-medium text-ink">{item.title}</h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-ink/65">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* POUR QUI */}
      <section className="bg-linen px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="structures-pour-qui">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="overline-label">Pour quelles structures ?</p>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
                Partout où l'animal peut <span className="italic text-terracotta">ouvrir le dialogue.</span>
              </h2>
              <div className="mt-10 space-y-4">
                {STRUCTURES.map((s, i) => (
                  <div
                    key={s.label}
                    className="flex items-center gap-4 rounded-2xl border border-stone-border bg-cream px-6 py-4 transition-transform duration-300 hover:-translate-y-0.5"
                    data-testid={`structure-type-${i + 1}`}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                      <s.icon size={18} strokeWidth={1.8} />
                    </span>
                    <p className="font-serif text-lg text-ink">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15} y={50}>
              <div className="relative">
                <div className="absolute -right-5 -top-5 h-full w-full blob-1 bg-terracotta-soft" aria-hidden="true" />
                <div className="blob-1 relative aspect-[5/4] overflow-hidden shadow-[0_30px_60px_-30px_rgba(43,58,48,0.4)]">
                  <img
                    src="/photos/bueno-jardin.webp"
                    alt="Enfants apprenant à interagir avec Bueno dans le jardin"
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    style={{ objectPosition: "50% 60%" }}
                    data-testid="structures-image"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA SE PASSE */}
      <section className="px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="structures-etapes">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="overline-label">Comment ça se passe</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
              Simple, clair, <span className="italic text-terracotta">sur devis.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={0.1 * i} y={40}>
                <div className="h-full rounded-[2rem] border border-stone-border bg-white/70 p-10 transition-transform duration-300 hover:-translate-y-1.5" data-testid={`structures-step-${i + 1}`}>
                  <span className="font-serif text-6xl font-medium italic leading-none text-terracotta/25">{step.num}</span>
                  <h3 className="mt-5 font-serif text-2xl font-medium text-ink">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-forest-deep px-6 py-24 text-center sm:px-10 md:py-28" data-testid="structures-cta">
        <PawPrint className="absolute -left-10 top-10 h-44 w-44 text-cream/5" strokeWidth={1} aria-hidden="true" />
        <PawPrint className="absolute -bottom-10 -right-8 h-52 w-52 rotate-45 text-cream/5" strokeWidth={1} aria-hidden="true" />
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-cream sm:text-4xl md:text-5xl">
            Une structure, un projet ? <span className="italic text-sand">Parlons-en.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-cream/65">
            Décrivez-moi votre public et vos besoins : je vous réponds sous 48 h
            avec une première proposition.
          </p>
          <Link to="/contact" className="btn-primary mt-10" data-testid="structures-cta-button">
            Demander un devis <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
